import React, { useState, useEffect, useCallback } from 'react';
import { fetchDisputes, updateDisputeStatus, resolveDispute } from '../api';
import DisputeDetail from './DisputeDetail';
import './Disputes.css';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'awaiting_response', label: 'Awaiting Response' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' }
];

const STATUS_LABELS = {
  open: 'Open',
  under_review: 'Under Review',
  awaiting_response: 'Awaiting Response',
  resolved: 'Resolved',
  dismissed: 'Dismissed'
};

const ORDER_TYPE_LABELS = {
  project_quote: 'Project',
  custom_quote: 'Custom',
  blanks: 'Blanks'
};

const Disputes = ({ session, onLogout }) => {
  const [disputes, setDisputes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const accessToken = session?.access_token;

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchDisputes(accessToken, statusFilter);
      setDisputes(data);
    } catch (err) {
      if (err.message === 'FORBIDDEN') {
        onLogout();
        return;
      }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, statusFilter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (disputeId, status, adminNotes) => {
    await updateDisputeStatus(accessToken, { disputeId, status, adminNotes });
    setSelected(null);
    setSuccessMsg('Dispute status updated.');
    setTimeout(() => setSuccessMsg(''), 4000);
    load();
  };

  const handleResolve = async (disputeId, formData) => {
    const result = await resolveDispute(accessToken, { disputeId, ...formData });
    setSelected(null);
    setSuccessMsg(
      `Dispute ${formData.finalStatus === 'dismissed' ? 'dismissed' : 'resolved'}.` +
      (result.emailsSent ? ` ${result.emailsSent} part${result.emailsSent === 1 ? 'y' : 'ies'} notified by email.` : '')
    );
    setTimeout(() => setSuccessMsg(''), 5000);
    load();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusPill = (status) => {
    const cls = `admin-pill dispute-pill-${status || 'open'}`;
    return <span className={cls}>{STATUS_LABELS[status] || 'Open'}</span>;
  };

  const handleExport = () => {
    if (disputes.length === 0) return;
    const headers = 'Date,Order,Type,Category,Client,Manufacturer,Status,Resolution,Amount';
    const rows = disputes.map(d => {
      const date = d.created_at ? new Date(d.created_at).toISOString().split('T')[0] : '';
      return `${date},"${d._orderTitle || ''}",${ORDER_TYPE_LABELS[d.order_type] || d.order_type},"${d._categoryLabel || d.category}","${d._clientName || ''}","${d._manufacturerName || ''}",${d.status},${d.resolution_type || ''},${d.resolution_amount || ''}`;
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tchpack-disputes-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredDisputes = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return disputes;
    return disputes.filter((d) => {
      const haystack = [
        d.report_number,
        d._quoteNumber,
        d.order_id,
        d._orderTitle,
        d._clientName,
        d._manufacturerName,
        d._categoryLabel || d.category
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  })();

  const openCount = disputes.filter(d => d.status === 'open').length;
  const reviewCount = disputes.filter(d => d.status === 'under_review' || d.status === 'awaiting_response').length;
  const resolvedCount = disputes.filter(d => d.status === 'resolved').length;

  return (
    <>
      {successMsg && (
        <div className="admin-success-banner">{successMsg}</div>
      )}

      <main className="admin-main">
        {/* Summary Bar */}
        {!selected && (
        <div className="admin-summary">
          <div className="admin-summary-card">
            <span className="admin-summary-label">Open</span>
            <span className="admin-summary-value">{openCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">In Progress</span>
            <span className="admin-summary-value">{reviewCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Resolved</span>
            <span className="admin-summary-value">{resolvedCount}</span>
          </div>
          <div className="admin-summary-card">
            <span className="admin-summary-label">Total Disputes</span>
            <span className="admin-summary-value">{disputes.length}</span>
          </div>
        </div>
        )}

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-table-wrapper">
          {selected ? (
            <DisputeDetail
              dispute={selected}
              onBack={() => setSelected(null)}
              onStatusChange={handleStatusChange}
              onResolve={handleResolve}
            />
          ) : (
          <>
          <div className="admin-table-header-bar">
            <h2>Order Disputes</h2>
            <div className="admin-table-header-right">
              <div className="admin-search-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search RPT, TPCQ/TPPQ, client, manufacturer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
                    ×
                  </button>
                )}
              </div>
              <button className="admin-refresh-btn" onClick={load} disabled={isLoading}>
                Refresh
              </button>
              <button className="admin-export-btn" onClick={handleExport} disabled={disputes.length === 0}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
              <span className="admin-table-count">{filteredDisputes.length} items</span>
            </div>
          </div>

          {/* Status Tabs */}
          <div className="admin-tabs">
            {STATUS_TABS.map(tab => (
              <button
                key={tab.value}
                className={`admin-tab ${statusFilter === tab.value ? 'admin-tab-active' : ''}`}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
                {tab.value === 'open' && openCount > 0 && (
                  <span className="admin-tab-badge">{openCount}</span>
                )}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="admin-loading">Loading disputes...</div>
          ) : filteredDisputes.length === 0 ? (
            <div className="admin-empty">
              <h3>{searchQuery ? 'No matching disputes' : statusFilter ? `No ${STATUS_LABELS[statusFilter]?.toLowerCase() || statusFilter} disputes` : 'No disputes yet'}</h3>
              <p>{searchQuery ? 'Try a different report number, quote number, client, or manufacturer.' : statusFilter === 'open' ? 'All caught up! No open disputes to review.' : 'Disputes will appear here when buyers report an issue with an order.'}</p>
            </div>
          ) : (
            <>
              <div className="admin-table-head">
                <span className="admin-col dispute-col-order">Order</span>
                <span className="admin-col dispute-col-category">Issue</span>
                <span className="admin-col dispute-col-client">Client</span>
                <span className="admin-col dispute-col-manu">Manufacturer</span>
                <span className="admin-col dispute-col-status">Status</span>
                <span className="admin-col dispute-col-date">Reported</span>
                <span className="admin-col dispute-col-action">Action</span>
              </div>

              {filteredDisputes.map((d) => (
                <div key={d.id} className={`admin-table-row ${d.status === 'open' ? 'admin-row-pending' : ''}`}>
                  <span className="admin-col dispute-col-order">
                    <span className="admin-manu-name">{d._orderTitle || 'Order'}</span>
                    <span className="admin-quote-type">
                      {d.report_number ? `${d.report_number} · ` : ''}
                      {ORDER_TYPE_LABELS[d.order_type] || d.order_type}
                      {d._quoteNumber ? ` · ${d._quoteNumber}` : ''}
                    </span>
                  </span>
                  <span className="admin-col dispute-col-category">
                    {d._categoryLabel || d.category}
                  </span>
                  <span className="admin-col dispute-col-client">
                    {d._clientName || '—'}
                  </span>
                  <span className="admin-col dispute-col-manu">
                    {d._manufacturerName || '—'}
                  </span>
                  <span className="admin-col dispute-col-status">
                    {getStatusPill(d.status)}
                  </span>
                  <span className="admin-col dispute-col-date">
                    {formatDate(d.created_at)}
                  </span>
                  <span className="admin-col dispute-col-action">
                    <button className="admin-record-btn" onClick={() => setSelected(d)}>
                      Review
                    </button>
                  </span>
                </div>
              ))}
            </>
          )}
          </>
          )}
        </div>
      </main>
    </>
  );
};

export default Disputes;
